import { useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  DateInput,
  Field,
  Input,
  Button,
} from '@truecare/ui';
import type {
  AdminFacilityContractAmendRequest,
  AdminFacilityContractRow,
  AdminFacilityDetailResponse,
  AdminFacilityStatusResponse,
  AdminFacilityUserRow,
  ClearCreditHoldResponse,
  FacilityCapability,
} from '@truecare/schemas';
import { presetForCapabilities } from '@truecare/schemas';
import { ApiError, TotpStepUpRequiredError, unwrap } from '@truecare/api-client';
import { api } from '../../api';
import { actionErrorMessage } from '../../lib/action-error';
import { adminBeforeLoad } from '../../lib/admin-before-load';
import { AdminLayout } from '../../components/AdminLayout';
import { AuditTrailPanel } from '../../components/AuditTrailPanel';
import { InvoicesCard } from '../../components/InvoicesCard';
import { ShiftsCard } from '../../components/ShiftsCard';
import { DangerButton } from '../../components/DangerButton';
import { OverrideActionButton } from '../../components/OverrideActionButton';

/**
 * Phase 14b-2 — Facility detail page.
 *
 * Single-scroll layout per 14b-2-1. Action cards per concern per
 * 14b-2-3:
 *   1. Identity (read-only)
 *   2. Status (activate / suspend / unsuspend / terminate)
 *   3. Current contract (read-only summary; amend form deferred to v1.x
 *      — terms are negotiated offline; the existing POST /contract
 *      endpoint stays usable from API tooling)
 *   4. Credit hold (clear when WARNED or ACTIVE — Phase 10e endpoint)
 *   5. Portal users (invite / remove)
 *   6. AuditTrailPanel at bottom per 14b-6
 */

export const Route = createFileRoute('/facilities/$id')({
  beforeLoad: ({ params }) => adminBeforeLoad(`/facilities/${params.id}`),
  component: FacilityDetailPage,
});

function FacilityDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detailKey = ['admin', 'facilities', id] as const;
  const detail = useQuery<AdminFacilityDetailResponse>({
    queryKey: detailKey,
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/facilities/{id}', {
          params: { path: { id } },
        } as never),
      )) as unknown as AdminFacilityDetailResponse,
  });

  function invalidate(): void {
    queryClient.invalidateQueries({ queryKey: detailKey });
  }

  const activate = useMutation({
    mutationFn: async () =>
      (await unwrap(
        api.POST('/api/v1/admin/facilities/{id}/activate', {
          params: { path: { id } },
          body: {},
        } as never),
      )) as unknown as AdminFacilityStatusResponse,
    onSuccess: invalidate,
  });

  const suspend = useMutation({
    mutationFn: async (reason: string) =>
      (await unwrap(
        api.POST('/api/v1/admin/facilities/{id}/suspend', {
          params: { path: { id } },
          body: { reason },
        } as never),
      )) as unknown as AdminFacilityStatusResponse,
    onSuccess: invalidate,
  });

  const unsuspend = useMutation({
    mutationFn: async () =>
      (await unwrap(
        api.POST('/api/v1/admin/facilities/{id}/unsuspend', {
          params: { path: { id } },
          body: {},
        } as never),
      )) as unknown as AdminFacilityStatusResponse,
    onSuccess: invalidate,
  });

  const terminate = useMutation({
    mutationFn: async (reason: string) =>
      (await unwrap(
        api.POST('/api/v1/admin/facilities/{id}/terminate', {
          params: { path: { id } },
          body: { reason },
        } as never),
      )) as unknown as AdminFacilityStatusResponse,
    // Bug #10 — terminate is terminal + irreversible; there's nothing more to do
    // on the detail page. Invalidate the facilities cache (prefix catches both
    // this detail and the list) and return to the list, where the row now shows
    // TERMINATED — rather than stranding the operator on a dead detail page.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'facilities'] });
      void navigate({ to: '/facilities' as string });
    },
  });

  // 14b-2 post-audit-round-3 H2 — request body MUST carry `{ reason }`
  // (≥10 chars, ≤500) per `ClearCreditHoldRequestSchema` at
  // packages/schemas/src/billing/dunning-admin.schema.ts:41-46. The
  // prior shape `body: {}` returned 400 on every click. SAME root
  // cause as the payout-frequency H1 in caregivers/$id.tsx — openapi-
  // fetch's `Client<paths>` typing does NOT strictly enforce request-
  // body shape, so typecheck didn't catch this. Phase 14b-3 Playwright
  // is the structural defense (will walk the actual UI flow through
  // the network layer); until it ships, the lesson is: keep body
  // literals aligned by hand against the Zod schemas.
  const clearHold = useMutation({
    mutationFn: async (reason: string) =>
      (await unwrap(
        api.POST('/api/v1/admin/facilities/{id}/credit-hold/clear', {
          params: { path: { id } },
          body: { reason },
        } as never),
      )) as unknown as ClearCreditHoldResponse,
    onSuccess: invalidate,
  });

  // Prelaunch fix (2026-08-11) — the create/amend-contract UI. Previously the
  // contract card was read-only and the POST /contract endpoint had NO UI, so
  // an operator could never create a facility's FIRST contract from the admin
  // app — which blocked activation entirely (Activate requires an active
  // contract). This form closes that gap. TotpFreshGuard on the endpoint is
  // handled globally (TotpStepUpModalProvider intercepts + replays).
  const amendContract = useMutation({
    mutationFn: async (body: AdminFacilityContractAmendRequest) =>
      (await unwrap(
        api.POST('/api/v1/admin/facilities/{id}/contract', {
          params: { path: { id } },
          body,
        } as never),
      )) as unknown as AdminFacilityContractRow,
    onSuccess: invalidate,
  });

  // Operator user-management mutations were removed with the read-only UsersCard
  // (facility-user self-service): facility Admins manage their own team from the
  // portal. The /admin/facilities/:id/users endpoints remain as break-glass
  // recovery — reachable via API, intentionally unwired in this UI.

  if (detail.isPending) {
    return (
      <AdminLayout>
        <p className="text-sm text-ink-muted">Loading facility…</p>
      </AdminLayout>
    );
  }

  if (detail.isError) {
    const status = detail.error instanceof ApiError ? detail.error.status : 0;
    return (
      <AdminLayout>
        <p className="text-sm text-danger">
          Failed to load facility {status ? `(${status})` : ''}.
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-4"
          onClick={() => navigate({ to: '/facilities' as string })}
        >
          Back to list
        </Button>
      </AdminLayout>
    );
  }

  const f = detail.data;
  const currentContract = f.contracts.find((c) => c.effectiveTo === null) ?? null;

  return (
    <AdminLayout>
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">{f.name}</h1>
          <p className="mt-1 text-sm text-ink-muted">
            {f.type.replaceAll('_', ' ').toLowerCase()} ·{' '}
            {f.code ? (
              <span className="font-mono">{f.code}</span>
            ) : (
              <span>no code</span>
            )}
          </p>
        </div>
        <StatusBadge status={f.status} />
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="mt-2"
        onClick={() => navigate({ to: '/facilities' as string })}
      >
        ← Back to list
      </Button>

      <IdentityCard data={f} onEmailChanged={invalidate} />

      <StatusActionsCard
        status={f.status}
        suspendedReason={f.suspensionReason}
        onActivate={() => activate.mutateAsync()}
        onSuspend={(reason) => suspend.mutateAsync(reason)}
        onUnsuspend={() => unsuspend.mutateAsync()}
        onTerminate={(reason) => terminate.mutateAsync(reason)}
        isActivatePending={activate.isPending}
        isSuspendPending={suspend.isPending}
        isUnsuspendPending={unsuspend.isPending}
        isTerminatePending={terminate.isPending}
      />

      <CurrentContractCard
        contract={currentContract}
        facilityId={f.id}
        onAmend={(body) => amendContract.mutateAsync(body)}
        isPending={amendContract.isPending}
      />

      <CreditHoldCard
        creditHoldStatus={f.creditHoldStatus}
        onClearHold={(reason) => clearHold.mutateAsync(reason)}
        isClearPending={clearHold.isPending}
      />

      <UsersCard users={f.users} />

      <ShiftsCard persona="facility" entityId={f.id} />

      <InvoicesCard facilityId={f.id} />

      <Card padding="md" className="mt-6" data-testid="facility-audit-card">
        <CardHeader title="Audit trail" />
        <AuditTrailPanel
          mode="entity-scoped"
          filters={{
            entityType: 'facility',
            entityId: f.id,
            page: 1,
            limit: 25,
          }}
        />
      </Card>
    </AdminLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function StatusBadge({
  status,
  testId = 'facility-status-badge',
}: {
  status: string;
  testId?: string;
}) {
  const tone =
    status === 'ACTIVE'
      ? 'bg-forest/10 text-forest'
      : status === 'PROSPECT'
        ? 'bg-cream text-ink-muted border border-border'
        : status === 'SUSPENDED'
          ? 'bg-gold/10 text-gold'
          : 'bg-danger/10 text-danger';
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-semibold ${tone}`}
      data-testid={testId}
    >
      {status}
    </span>
  );
}

function IdentityCard({
  data,
  onEmailChanged,
}: {
  data: AdminFacilityDetailResponse;
  onEmailChanged: () => void;
}) {
  const addressLines = [
    data.addressLine1,
    data.addressLine2,
    [data.city, data.province, data.postalCode].filter(Boolean).join(' '),
  ].filter((s): s is string => Boolean(s));
  return (
    <Card padding="md" className="mt-6" data-testid="facility-identity-card">
      <CardHeader title="Facility details" />
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Row label="Legal name" value={data.name} />
        <Row label="DBA" value={data.dbaName} />
        <Row label="Type" value={data.type} />
        <Row
          label="Address"
          value={addressLines.length > 0 ? addressLines.join(', ') : null}
        />
        <Row label="Primary contact" value={data.primaryContactName} />
        <PrimaryEmailField
          facilityId={data.id}
          email={data.primaryContactEmail}
          editable={data.primaryContactEmailEditable}
          onChanged={onEmailChanged}
        />
        <Row label="Primary phone" value={data.primaryContactPhone} />
        <Row label="Billing email" value={data.billingContactEmail} />
        <HstTreatmentField
          facilityId={data.id}
          hstTreatment={data.hstTreatment}
          onChanged={onEmailChanged}
        />
        <Row
          label="Created"
          value={new Date(data.createdAt).toLocaleString('en-CA')}
        />
        <Row
          label="Activated"
          value={
            data.activatedAt
              ? new Date(data.activatedAt).toLocaleString('en-CA')
              : null
          }
        />
      </dl>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-ink">
        {value ?? <span className="text-ink-muted">—</span>}
      </dd>
    </div>
  );
}

// Per-facility HST treatment (2026-09-21) — same enum as the rate card.
const HST_TREATMENT_OPTIONS = [
  { value: 'TAXABLE', label: 'Taxable (13% HST)' },
  { value: 'EXEMPT', label: 'Exempt (no HST)' },
  { value: 'ZERO_RATED', label: 'Zero-rated (0%)' },
] as const;

function hstTreatmentLabel(v: string): string {
  return HST_TREATMENT_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

/**
 * HST treatment — the facility's tax status (per-facility, uniform across all roles;
 * operator decision 2026-09-21). Inline-editable select. Saving PATCHes the facility
 * (TotpFresh-guarded, so a stale operator session triggers the global TOTP step-up).
 * Forward-only: past shifts keep their snapshotted hstTreatmentAtBooking; only future
 * bookings pick up the new treatment.
 */
function HstTreatmentField({
  facilityId,
  hstTreatment,
  onChanged,
}: {
  facilityId: string;
  hstTreatment: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(hstTreatment);

  const change = useMutation({
    mutationFn: async (next: string) =>
      (await unwrap(
        api.PATCH('/api/v1/admin/facilities/{id}', {
          params: { path: { id: facilityId } },
          body: { hstTreatment: next },
        } as never),
      )) as unknown as AdminFacilityDetailResponse,
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
  });

  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">HST treatment</dt>
      <dd className="mt-0.5 text-sm text-ink">
        {editing ? (
          <div className="flex flex-col gap-2">
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full rounded-md border border-border bg-cream p-2 text-sm"
              data-testid="facility-hst-treatment-input"
            >
              {HST_TREATMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <Button
                size="sm"
                loading={change.isPending}
                onClick={() => change.mutate(value)}
                data-testid="facility-hst-treatment-save"
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  setValue(hstTreatment);
                  change.reset();
                }}
              >
                Cancel
              </Button>
            </div>
            {change.isError && (
              <p
                className="text-xs text-danger"
                data-testid="facility-hst-treatment-error"
              >
                {actionErrorMessage(change.error)}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span>{hstTreatmentLabel(hstTreatment)}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setValue(hstTreatment);
                change.reset();
                setEditing(true);
              }}
              data-testid="facility-hst-treatment-edit"
            >
              Change
            </Button>
          </div>
        )}
      </dd>
    </div>
  );
}

/**
 * Primary contact email — read-only once the contact has signed in, editable
 * (inline) while they are still a never-used invitee. Saving PATCHes the
 * facility; the backend re-points the pending invite to the new address and
 * sends a fresh magic-link. The endpoint is TotpFresh-guarded, so a stale
 * operator session triggers the global TOTP step-up modal + replay
 * automatically (TotpStepUpModalProvider) — no explicit handling here.
 */
function PrimaryEmailField({
  facilityId,
  email,
  editable,
  onChanged,
}: {
  facilityId: string;
  email: string | null;
  editable: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(email ?? '');
  const [changedTo, setChangedTo] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: async (newEmail: string) =>
      (await unwrap(
        api.PATCH('/api/v1/admin/facilities/{id}', {
          params: { path: { id: facilityId } },
          body: { primaryContactEmail: newEmail },
        } as never),
      )) as unknown as AdminFacilityDetailResponse,
    onSuccess: (_res, newEmail) => {
      setEditing(false);
      setChangedTo(newEmail);
      onChanged();
    },
  });

  // Signed-in contact → email is their login identity; read-only.
  if (!editable) {
    return <Row label="Primary email" value={email} />;
  }

  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">
        Primary email
      </dt>
      <dd className="mt-0.5 text-sm text-ink">
        {editing ? (
          <div className="flex flex-col gap-2">
            <Input
              type="email"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="facility-primary-email-input"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                loading={change.isPending}
                disabled={value.trim().length === 0}
                onClick={() => change.mutate(value.trim())}
                data-testid="facility-primary-email-save"
              >
                Save + resend invite
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  change.reset();
                }}
              >
                Cancel
              </Button>
            </div>
            {change.isError && (
              <p
                className="text-xs text-danger"
                data-testid="facility-primary-email-error"
              >
                {actionErrorMessage(change.error)}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span>{email ?? <span className="text-ink-muted">—</span>}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setValue(email ?? '');
                setChangedTo(null);
                change.reset();
                setEditing(true);
              }}
              data-testid="facility-primary-email-edit"
            >
              Change
            </Button>
          </div>
        )}
        {changedTo && !editing && (
          <p
            className="mt-1 text-xs text-success"
            data-testid="facility-primary-email-done"
          >
            Email updated to {changedTo}; a fresh invite was sent.
          </p>
        )}
        {!editing && (
          <p className="mt-1 text-xs text-ink-muted">
            The primary contact hasn’t signed in yet, so you can still correct
            their email.
          </p>
        )}
      </dd>
    </div>
  );
}

function StatusActionsCard(props: {
  status: string;
  suspendedReason: string | null;
  onActivate: () => Promise<unknown>;
  onSuspend: (reason: string) => Promise<unknown>;
  onUnsuspend: () => Promise<unknown>;
  onTerminate: (reason: string) => Promise<unknown>;
  isActivatePending: boolean;
  isSuspendPending: boolean;
  isUnsuspendPending: boolean;
  isTerminatePending: boolean;
}) {
  // H-F2 — surface failures inline; preserve typed reasons on failure.
  const [statusError, setStatusError] = useState<string | null>(null);
  // Suspend + Terminate both route through a confirmation modal that owns its own
  // reason + inline error. Terminate (irreversible) → typed-token <DangerButton>;
  // Suspend (reversible) → reason-only <OverrideActionButton>. No local state here.

  async function runStatus(action: () => Promise<unknown>): Promise<void> {
    setStatusError(null);
    try {
      await action();
    } catch (e) {
      const msg = actionErrorMessage(e);
      if (msg) setStatusError(msg);
    }
  }

  const isProspect = props.status === 'PROSPECT';
  const isActive = props.status === 'ACTIVE';
  const isSuspended = props.status === 'SUSPENDED';
  const isTerminated = props.status === 'TERMINATED';

  return (
    <Card padding="md" className="mt-6" data-testid="facility-status-card">
      <CardHeader title="Status" />

      <p
        className="mb-4 flex items-center gap-2 text-sm text-ink-muted"
        data-testid="facility-status-current"
      >
        Current status:{' '}
        <StatusBadge status={props.status} testId="facility-status-card-badge" />
      </p>

      {isSuspended && props.suspendedReason && (
        <p className="mb-4 rounded-md bg-gold/10 px-3 py-2 text-sm text-gold">
          <strong>Suspended:</strong> {props.suspendedReason}
        </p>
      )}

      {statusError && (
        <p
          className="mb-4 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger"
          data-testid="facility-status-error"
        >
          {statusError}
        </p>
      )}

      <div className="flex flex-col gap-4">
        {/* Phase 14b-2 post-audit W1 — Activate is the PROSPECT → ACTIVE
            path (first-time contract-complete activation). SUSPENDED →
            ACTIVE uses the dedicated Unsuspend button below; rendering
            both simultaneously on SUSPENDED was operator-confusing
            because the two paths have different semantics (Activate
            runs activation prerequisites; Unsuspend is unconditional). */}
        {isProspect && (
          <div>
            <Button
              size="sm"
              variant="primary"
              loading={props.isActivatePending}
              onClick={() => void runStatus(props.onActivate)}
              data-testid="facility-activate-button"
            >
              Activate
            </Button>
            <p className="mt-1 text-xs text-ink-muted">
              Opens revenue path: enables shift requests + dunning ladder.
            </p>
          </div>
        )}

        {isActive && (
          <div className="border-l-4 border-gold pl-3">
            <OverrideActionButton
              actionLabel="Suspend facility"
              reasonLabel="Suspension reason (operator-only)"
              onConfirm={async ({ reason }) => {
                await props.onSuspend(reason);
              }}
              loading={props.isSuspendPending}
              data-testid="facility-suspend-button"
              instructions={
                <>
                  Blocks the facility from posting new shift requests (existing
                  bookings continue and still bill). Reversible via Unsuspend.
                </>
              }
            >
              Suspend facility
            </OverrideActionButton>
            <p className="mt-1 text-xs text-ink-muted">
              Blocks new requests; reversible. Reason is recorded.
            </p>
          </div>
        )}

        {isSuspended && (
          <div>
            <Button
              size="sm"
              variant="secondary"
              loading={props.isUnsuspendPending}
              onClick={() => void runStatus(props.onUnsuspend)}
              data-testid="facility-unsuspend-button"
            >
              Unsuspend
            </Button>
          </div>
        )}

        {/* Terminate is irreversible + the highest-blast-radius action on this
            page, so it carries the strongest guardrail: the same typed-token
            <DangerButton> modal as Void / Force-close (type "TERMINATE" + a
            reason ≥10 chars), not a bare reason field + red button. */}
        {!isTerminated && (
          <div className="border-l-4 border-danger pl-3">
            <DangerButton
              entityName="TERMINATE"
              confirmationToken="TERMINATE"
              actionLabel="Terminate facility"
              onConfirm={async ({ reason }) => {
                await props.onTerminate(reason ?? '');
              }}
              loading={props.isTerminatePending}
              data-testid="facility-terminate-button"
            >
              Terminate facility
            </DangerButton>
            <p className="mt-1 text-xs text-ink-muted">
              Irreversible — ends the billing relationship. Reason is recorded.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

const CADENCE_OPTIONS = ['WEEKLY', 'BIWEEKLY', 'MONTHLY'] as const;
const DUNNING_OPTIONS = ['STANDARD', 'LENIENT', 'STRICT'] as const;
const selectClass = 'w-full rounded-md border border-border bg-cream p-2 text-sm';

/** Today as YYYY-MM-DD in the operator's local date (en-CA formats ISO-style). */
function todayLocalIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

function CurrentContractCard({
  contract,
  facilityId,
  onAmend,
  isPending,
}: {
  contract: AdminFacilityContractRow | null;
  facilityId: string;
  onAmend: (body: AdminFacilityContractAmendRequest) => Promise<unknown>;
  isPending: boolean;
}) {
  // When there's no contract, the form is shown immediately (it's the
  // activation blocker). When one exists, it's behind an "Amend" toggle so the
  // summary stays the default view.
  const [showForm, setShowForm] = useState(false);
  const formOpen = contract === null || showForm;

  return (
    <Card padding="md" className="mt-6" data-testid="facility-contract-card">
      <CardHeader
        title="Current contract"
        description="Billing terms in effect. Recorded by the operator — terms are negotiated offline, then entered here. Amending creates a new versioned row and closes the prior one."
      />
      {contract === null ? (
        <p className="mb-4 text-sm text-ink-muted" data-testid="facility-no-contract">
          No active contract yet — a facility can&apos;t be activated until one
          is recorded. Add it below.
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Row label="Effective from" value={contract.effectiveFrom} />
            <Row label="PO reference" value={contract.poReference} />
            <Row label="Invoice cadence" value={contract.invoiceCadence} />
            <Row label="Net terms" value={`${contract.netTermsDays} days`} />
            <Row label="Dunning profile" value={contract.dunningProfile} />
            <Row
              label="Interest / month"
              value={`${contract.interestPerMonthPct}%`}
            />
          </dl>
          {!showForm && (
            <div className="mt-4">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowForm(true)}
                data-testid="facility-amend-contract-toggle"
              >
                Amend contract
              </Button>
            </div>
          )}
          <SignedContractDocSection
            facilityId={facilityId}
            hasDoc={contract.contractDocKey !== null}
          />
        </>
      )}

      {formOpen && (
        <ContractForm
          existing={contract}
          onAmend={onAmend}
          isPending={isPending}
          onDone={() => setShowForm(false)}
          onCancel={contract === null ? undefined : () => setShowForm(false)}
        />
      )}
    </Card>
  );
}

/** Create (no contract) or amend (versioned) a facility contract. Seeds from
 *  the existing contract's terms when amending; effective-from defaults to
 *  today for a new row. */
function ContractForm({
  existing,
  onAmend,
  isPending,
  onDone,
  onCancel,
}: {
  existing: AdminFacilityContractRow | null;
  onAmend: (body: AdminFacilityContractAmendRequest) => Promise<unknown>;
  isPending: boolean;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const [effectiveFrom, setEffectiveFrom] = useState(todayLocalIso());
  const [poReference, setPoReference] = useState(existing?.poReference ?? '');
  const [invoiceCadence, setInvoiceCadence] = useState<string>(
    existing?.invoiceCadence ?? 'BIWEEKLY',
  );
  const [netTermsDays, setNetTermsDays] = useState<string>(
    existing ? String(existing.netTermsDays) : '30',
  );
  const [dunningProfile, setDunningProfile] = useState<string>(
    existing?.dunningProfile ?? 'STANDARD',
  );
  const [interestPerMonthPct, setInterestPerMonthPct] = useState<string>(
    existing ? String(existing.interestPerMonthPct) : '1.5',
  );
  const [error, setError] = useState<string | null>(null);

  const isNew = existing === null;

  function submit(): void {
    setError(null);
    const body: AdminFacilityContractAmendRequest = {
      effectiveFrom,
      poReference: poReference.trim() === '' ? null : poReference.trim(),
      invoiceCadence: invoiceCadence as AdminFacilityContractAmendRequest['invoiceCadence'],
      netTermsDays: Number(netTermsDays),
      dunningProfile: dunningProfile as AdminFacilityContractAmendRequest['dunningProfile'],
      interestPerMonthPct: Number(interestPerMonthPct),
    };
    onAmend(body).then(
      () => {
        onDone();
      },
      (err: unknown) => {
        // TotpStepUpRequiredError is handled globally (modal + replay) — never
        // surface it as a form error.
        if (err instanceof TotpStepUpRequiredError) return;
        const msg = actionErrorMessage(err);
        if (msg) setError(msg);
      },
    );
  }

  return (
    <div
      className="mt-4 border-t border-border pt-4"
      data-testid="facility-contract-form"
    >
      <p className="mb-3 text-sm font-semibold text-ink">
        {isNew ? 'Add contract' : 'Amend contract (new version)'}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Effective from">
          <DateInput
            value={effectiveFrom}
            onChange={setEffectiveFrom}
            data-testid="facility-contract-effective-from"
          />
        </Field>
        <Field label="PO reference (optional)">
          <Input
            type="text"
            value={poReference}
            onChange={(e) => setPoReference(e.target.value)}
            placeholder="Facility PO number, if any"
            data-testid="facility-contract-po-reference"
          />
        </Field>
        <Field label="Invoice cadence">
          <select
            value={invoiceCadence}
            onChange={(e) => setInvoiceCadence(e.target.value)}
            className={selectClass}
            data-testid="facility-contract-invoice-cadence"
          >
            {CADENCE_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o.charAt(0) + o.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Net terms (days)">
          <Input
            type="number"
            min={1}
            max={120}
            value={netTermsDays}
            onChange={(e) => setNetTermsDays(e.target.value)}
            data-testid="facility-contract-net-terms"
          />
        </Field>
        <Field label="Dunning profile">
          <select
            value={dunningProfile}
            onChange={(e) => setDunningProfile(e.target.value)}
            className={selectClass}
            data-testid="facility-contract-dunning-profile"
          >
            {DUNNING_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o.charAt(0) + o.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Interest / month (%)">
          <Input
            type="number"
            min={0}
            max={5}
            step={0.1}
            value={interestPerMonthPct}
            onChange={(e) => setInterestPerMonthPct(e.target.value)}
            data-testid="facility-contract-interest"
          />
        </Field>
      </div>

      {error && (
        <p className="mt-3 text-sm text-danger" data-testid="facility-contract-error">
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <Button
          size="sm"
          loading={isPending}
          onClick={submit}
          data-testid="facility-contract-submit"
        >
          {isNew ? 'Add contract' : 'Save amendment'}
        </Button>
        {onCancel && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancel}
            data-testid="facility-contract-cancel"
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

/** Operator upload / download of the signed contract PDF. Upload is a 3-step
 *  flow (presigned URL → PUT to storage → attach to current contract) wrapped
 *  in ONE mutation so a TotpStepUpRequiredError re-runs the whole sequence
 *  cleanly (the File is the mutation variable, preserved across the replay). */
function SignedContractDocSection({
  facilityId,
  hasDoc,
}: {
  facilityId: string;
  hasDoc: boolean;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { uploadUrl, objectStorageKey } = (await unwrap(
        api.POST('/api/v1/admin/facilities/{id}/contract/doc-upload-url', {
          params: { path: { id: facilityId } },
          body: { contentType: 'application/pdf', contentLength: file.size },
        } as never),
      )) as unknown as { uploadUrl: string; objectStorageKey: string };
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'application/pdf' },
      });
      if (!put.ok) throw new Error(`Storage upload failed (${put.status})`);
      await unwrap(
        api.PUT('/api/v1/admin/facilities/{id}/contract/doc', {
          params: { path: { id: facilityId } },
          body: { objectStorageKey },
        } as never),
      );
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'facilities', facilityId] });
    },
    onError: (err: unknown) => {
      // Global provider handles step-up (modal + replay of the whole mutation).
      if (err instanceof TotpStepUpRequiredError) return;
      const msg = actionErrorMessage(err) ?? (err instanceof Error ? err.message : null);
      if (msg) setError(msg);
    },
  });

  async function download(): Promise<void> {
    setError(null);
    try {
      const { downloadUrl } = (await unwrap(
        api.GET('/api/v1/admin/facilities/{id}/contract/doc', {
          params: { path: { id: facilityId } },
        } as never),
      )) as unknown as { downloadUrl: string };
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      const msg = actionErrorMessage(err);
      if (msg) setError(msg);
    }
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file) upload.mutate(file);
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <div
      className="mt-4 border-t border-border pt-4"
      data-testid="facility-contract-doc-section"
    >
      <p className="mb-2 text-sm font-semibold text-ink">Signed contract PDF</p>
      <div className="flex flex-wrap items-center gap-2">
        {hasDoc ? (
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={download}
              data-testid="facility-contract-doc-download"
            >
              Download
            </Button>
            <Button
              size="sm"
              variant="ghost"
              loading={upload.isPending}
              onClick={() => fileRef.current?.click()}
              data-testid="facility-contract-doc-replace"
            >
              Replace
            </Button>
            <span className="text-xs text-ink-muted">
              On file — the facility can download it from their portal.
            </span>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="secondary"
              loading={upload.isPending}
              onClick={() => fileRef.current?.click()}
              data-testid="facility-contract-doc-upload"
            >
              Upload signed PDF
            </Button>
            <span className="text-xs text-ink-muted">
              Optional — attach the executed agreement (PDF). The facility gets a
              read-only download.
            </span>
          </>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={onFile}
        data-testid="facility-contract-doc-input"
      />
      {error && (
        <p className="mt-2 text-sm text-danger" data-testid="facility-contract-doc-error">
          {error}
        </p>
      )}
    </div>
  );
}

function CreditHoldCard(props: {
  creditHoldStatus: string;
  onClearHold: (reason: string) => Promise<unknown>;
  isClearPending: boolean;
}) {
  // 14b-2 post-audit-round-3 H2 — operator-typed reason required for
  // the credit-hold-clear audit row per Phase 10e. Min 10 chars (the
  // backend ClearCreditHoldRequestSchema's `ReasonSchema` floor).
  const [clearReason, setClearReason] = useState('');
  // H-F2 — surface failures inline; preserve typed reason on failure.
  const [clearError, setClearError] = useState<string | null>(null);
  const isOnHold =
    props.creditHoldStatus === 'WARNED' || props.creditHoldStatus === 'ACTIVE';
  return (
    <Card padding="md" className="mt-6" data-testid="facility-credit-hold-card">
      <CardHeader title="Credit hold" />
      <p className="mb-3 text-sm">
        Current state:{' '}
        <span
          className={
            props.creditHoldStatus === 'NONE'
              ? 'text-ink-muted'
              : props.creditHoldStatus === 'WARNED'
                ? 'text-gold font-semibold'
                : 'text-danger font-semibold'
          }
        >
          {props.creditHoldStatus}
        </span>
      </p>
      {isOnHold && (
        <>
          <Field label="Reason (operator-only; audit-event payload)">
            <Input
              value={clearReason}
              onChange={(e) => setClearReason(e.target.value)}
              placeholder="e.g. AR paid in full via EFT received today"
              data-testid="facility-clear-hold-reason"
            />
          </Field>
          {clearError && (
            <p className="mt-2 text-sm text-danger" data-testid="facility-clear-hold-error">
              {clearError}
            </p>
          )}
          <div className="mt-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={clearReason.trim().length < 10}
              loading={props.isClearPending}
              onClick={() => {
                const reason = clearReason.trim();
                setClearError(null);
                props.onClearHold(reason).then(
                  () => setClearReason(''),
                  (err: unknown) => {
                    const msg = actionErrorMessage(err);
                    if (msg) setClearError(msg);
                  },
                );
              }}
              data-testid="facility-clear-hold-button"
            >
              Clear credit hold
            </Button>
          </div>
        </>
      )}
      {!isOnHold && (
        <p className="text-sm text-ink-muted">
          Facility is in good standing. Hold is set automatically by the
          dunning scan when invoices reach +14d (WARNED) or +30d past due
          (ACTIVE).
        </p>
      )}
    </Card>
  );
}

/** Short label for a login's current access: the matching preset name, or
 *  "Custom (N)" for a bespoke set the raw editor (v1.x) could produce. */
function roleLabel(capabilities: FacilityCapability[]): string {
  const preset = presetForCapabilities(capabilities);
  if (preset) return preset.charAt(0) + preset.slice(1).toLowerCase();
  return `Custom (${capabilities.length})`;
}

// Read-only in the operator app: facility Admins manage their own team from the
// portal (facility-user self-service). The operator only VIEWS the roster here;
// the first Admin is bootstrapped from the primary contact at facility create.
function UsersCard(props: { users: AdminFacilityUserRow[] }) {
  return (
    <Card padding="md" className="mt-6" data-testid="facility-users-card">
      <CardHeader
        title={`Portal users (${props.users.length})`}
        description="Read-only — facility Admins manage their own team from the portal. The first Admin is created from the primary contact when the facility is set up."
      />

      {props.users.length > 0 && (
        <ul className="mb-4 divide-y divide-border">
          {props.users.map((u) => (
            <li
              key={u.userId}
              className="flex items-center justify-between py-3"
              data-testid="facility-user-row"
            >
              <div>
                <p className="font-semibold text-ink">
                  {u.displayName ?? u.email}
                </p>
                <p className="text-xs text-ink-muted">{u.email}</p>
                <p className="mt-0.5 text-xs text-ink-muted" data-testid={`facility-user-role-${u.userId}`}>
                  Role: <span className="font-medium text-ink">{roleLabel(u.capabilities)}</span>
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
